import {
	useQuery,
	useInfiniteQuery,
	useQueryClient,
	useMutation,
} from "react-query";
import { toast } from "react-toastify";
import API from "./utils/api";
import {
	SalaryAdvanceGroupDto,
	SalaryAdvanceGroupPaginator,
	CreateSalaryAdvanceGroupDto,
	QuerySalaryAdvanceGroupDto,
	SearchSalaryAdvanceGroupDto,
	UpdateSalaryAdvanceGroupDto,
} from "../models/salary-advance-group.dto";
import ENDPOINTS from "./utils/endpoints";
import { QueryOptionsDto } from "../models/generic.dto";

export function useGetSalaryAdvanceGroup(
	companyId: number,
	options?: QuerySalaryAdvanceGroupDto
) {
	const url = ENDPOINTS.PROCESSING_COMPANY.SALARYADVANCEGROUP.GET;
	const request = (options: QuerySalaryAdvanceGroupDto) =>
		API.ProcessingCompanyAPI.getSalaryAdvanceGroups(companyId, options);

	const { data, isLoading, error, refetch } = useQuery<
		SalaryAdvanceGroupPaginator,
		Error
	>(
		[url, options],
		({ queryKey, pageParam }) =>
			request(Object.assign({}, queryKey[1], pageParam)),
		{ ...API.Handlers.pageParamOption }
	);

	return { data, isLoading, error, refetch };
}

export function useGetSalaryAdvanceGroupSearch() {
	return useMutation(
		(companyId: number, options?: SearchSalaryAdvanceGroupDto) =>
			API.ProcessingCompanyAPI.getSalaryAdvanceGroupSearch(companyId, options),
		{
			onSuccess: () => {
				//toast.success('Record created successfully');
			},
			onError: API.Handlers.onErrorHandler,
		}
	);
}

export function useGetSalaryAdvanceGroupById(companyId: number, id: number) {
	const url = ENDPOINTS.PROCESSING_COMPANY.SALARYADVANCEGROUP.DETAIL;
	const request = API.ProcessingCompanyAPI.getSalaryAdvanceGroupById(
		companyId,
		id
	);

	const { data, isLoading, error } = useQuery<
		{ data: SalaryAdvanceGroupDto },
		Error
	>([url, id], () => request, {
		enabled: !!id,
	});

	return {
		data: data,
		isLoading,
		error,
	};
}
export function useCreateSalaryAdvanceGroup() {
	const queryClient = useQueryClient();

	return useMutation(
		({
			companyId,
			payload,
		}: {
			companyId: number;
			payload: CreateSalaryAdvanceGroupDto;
		}) => API.ProcessingCompanyAPI.createSalaryAdvanceGroup(companyId, payload),
		{
			onSuccess: () => {
				toast.success("Record created successfully");
				queryClient.invalidateQueries(
					ENDPOINTS.PROCESSING_COMPANY.SALARYADVANCEGROUP.GET
				);
			},
			onError: API.Handlers.onErrorHandler,
		}
	);
}

export function useUpdateSalaryAdvanceGroup() {
	const queryClient = useQueryClient();

	return useMutation(
		({
			payload,
			companyId,
			id,
		}: {
			payload: UpdateSalaryAdvanceGroupDto;
			companyId: number;
			id: number;
		}) =>
			API.ProcessingCompanyAPI.updateSalaryAdvanceGroup(companyId, id, payload),
		{
			onSuccess: () => {
				toast.success("Record updated successfully");
				queryClient.invalidateQueries(
					ENDPOINTS.PROCESSING_COMPANY.SALARYADVANCEGROUP.GET
				);
			},
			onError: API.Handlers.onErrorHandler,
		}
	);
}

export function useDeleteSalaryAdvanceGroup() {
	return useMutation(
		({ companyId, id }: { companyId: number; id: number }) =>
			API.ProcessingCompanyAPI.deleteSalaryAdvanceGroupById(companyId, id),
		{
			onSuccess: () => {
				toast.success("Record deleted successfully");
			},
			onError: API.Handlers.onErrorHandler,
		}
	);
}
